/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useEffect, useState } from 'react';
import { Card, Grid, Alert, Snackbar, Chip } from '@mui/material';
import { Add, Delete, Edit, OpenInNew } from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import { useNavigate } from 'react-router-dom';
import { MAX_FIELDS, MAX_OBJECTS } from './customObjectsFieldTypes';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

export default function CustomObjectsSettings() {
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const isV2 = useTheme()?.pearlUi === "v2";
  const [objects, setObjects] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [objRes, limitsRes] = await Promise.all([
        axiosInstance.get('/custom-objects'),
        axiosInstance.get('/custom-objects-info/limits'),
      ]);
      setObjects(objRes.data || []);
      setLimits(limitsRes.data);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load custom objects', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const deleteObject = async (obj) => {
    if (!window.confirm(`Delete "${obj.name}" and all its records?`)) return;
    try {
      await axiosInstance.delete(`/custom-objects/${obj.id}`);
      setSnackbar({ open: true, message: 'Custom object deleted', severity: 'success' });
      fetchData();
      window.dispatchEvent(new CustomEvent('sidenavPreferencesUpdated'));
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete custom object', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <DataLoadingSpinner
        entityType="default"
        title="Loading Custom Objects"
        message="Please wait while we fetch your custom object settings..."
      />
    );
  }

  return (
    <MDBox>
      {isV2 ? (
        <PageHeader
          title="Custom Objects"
          summary={`Design tenant modules with up to ${MAX_OBJECTS} objects and ${MAX_FIELDS} fields each.${limits ? ` ${limits.current_objects} of ${limits.max_objects} objects used.` : ""}`}
          actions={(
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => navigate('/settings/custom-objects/new')}
              disabled={(limits?.current_objects || 0) >= MAX_OBJECTS}
            >
              <Add sx={{ mr: 1 }} /> New Object
            </MDButton>
          )}
        />
      ) : (
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDBox>
          <MDTypography variant="h5" fontWeight="medium">Custom Objects</MDTypography>
          <MDTypography variant="body2" color="text">
            Design tenant modules with up to {MAX_OBJECTS} objects and {MAX_FIELDS} fields each.
          </MDTypography>
          {limits && (
            <Chip
              size="small"
              color="info"
              label={`${limits.current_objects} of ${limits.max_objects} objects`}
              sx={{ mt: 1 }}
            />
          )}
        </MDBox>
        <MDButton
          color="info"
          onClick={() => navigate('/settings/custom-objects/new')}
          disabled={(limits?.current_objects || 0) >= MAX_OBJECTS}
        >
          <Add sx={{ mr: 1 }} /> New Object
        </MDButton>
      </MDBox>
      )}

      <Grid container spacing={2}>
        {objects.map((obj) => (
          <Grid item xs={12} md={6} key={obj.id}>
            <Card sx={{ p: 2, height: '100%' }}>
              <MDTypography variant="h6">{obj.pluralName || obj.name}</MDTypography>
              <MDTypography variant="body2" color="text" mb={1}>{obj.description || 'No description'}</MDTypography>
              <Chip size="small" label={`${obj.totalFields || 0} fields`} sx={{ mr: 1 }} />
              <Chip size="small" label={obj.apiName} variant="outlined" />
              <MDBox display="flex" gap={1} mt={2}>
                <MDButton size="small" color="info" onClick={() => navigate(`/custom-objects/${obj.apiName}`)}>
                  <OpenInNew fontSize="small" sx={{ mr: 0.5 }} /> View Data
                </MDButton>
                <MDButton size="small" color={isV2 ? "dark" : "secondary"} variant={isV2 ? "outlined" : undefined} onClick={() => navigate(`/settings/custom-objects/${obj.id}/edit`)}>
                  <Edit fontSize="small" sx={{ mr: 0.5 }} /> Edit
                </MDButton>
                <MDButton size="small" color="error" variant={isV2 ? "outlined" : undefined} onClick={() => deleteObject(obj)}>
                  <Delete fontSize="small" sx={{ mr: 0.5 }} /> Delete
                </MDButton>
              </MDBox>
            </Card>
          </Grid>
        ))}
        {!objects.length && (
          <Grid item xs={12}>
            <Alert severity="info">No custom objects yet. Create one to add CMS-style modules to your CRM.</Alert>
          </Grid>
        )}
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </MDBox>
  );
}
